import axios from 'axios';
import { getJwkKeystore, jweDecryption, jweEncryption } from '../utils/encryption';
import i18n from '../i18n';
export const confAxios = {
  baseURL: 
  'https://test-drivnest.grayfox.ma/api',
  //'http://localhost:7060/api',

  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

export const api = axios.create(confAxios);

export const apiBlob = axios.create({
  ...confAxios,
  responseType: 'blob',
});

const getToken = () => new Promise((resolve) => {
  resolve(localStorage.getItem('token'));
});

export const getResponseData = async (response, key, useJWE = true) => {
  if ([null, '', undefined].includes(response?.data) || !useJWE) {
    return response?.data;
  }
  if (process.env.REACT_APP_JWE === 'false') {
    return response?.data;
  }
  const data = await jweDecryption(response?.data, key);
  return data;
};

const checkNotAuthored = async (err, showSnackbar) => {
  if (err?.response?.status === 401) {
    //localStorage.clear();    
    //window.location.href = '/login';
    //return null;
  }

  // Skip error handling if request was canceled (AbortError)
  if (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || err.message === 'canceled') {
    throw err;
  }

  try {
    if (!err?.response) {
      showSnackbar(i18n.t('errors.generic'), 'error', true);
      throw err;
    }

    if (String(err.response.status)==='500' || String(err.response.status)==='502' || String(err.response.status)==='503' || String(err.response.status)==='504') {
      showSnackbar(i18n.t('errors.generic'), 'error', true);
      throw err;
    }

    if (err.response?.data) {
      const key = await getJwkKeystore();
      const decryptedErrorData = await getResponseData(err.response, key);
      err.response.data = decryptedErrorData;

      if (decryptedErrorData?.statusLabel) {
        if (typeof showSnackbar === 'function') {
          showSnackbar(decryptedErrorData.statusLabel, 'error', true);
        }
      } else {
        showSnackbar(i18n.t('errors.generic'), 'error', true);
      }
    } else {
      showSnackbar(i18n.t('errors.generic'), 'error', true);
    }
  } catch (e) {
    // Skip error handling if the caught error is also an AbortError
    if (e.name === 'AbortError' || e.code === 'ERR_CANCELED' || e.message === 'canceled') {
      throw err;
    }
    console.error('Error while handling error', e);
    showSnackbar(i18n.t('errors.generic'), 'error', true);
  }

  throw err;
};

export const secureGet = async (url, showSnackbar, options = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const conf = {
      headers: {
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json',
      },
      ...options, 
  };
  return api
    .get(`${url}`, conf)
    .then(async (response) => {
      const data = await getResponseData(response, key);
      return { ...response, data };
    })
    .catch(
      (err) => checkNotAuthored(err, showSnackbar));  
};

export const secureDelete = async (url, showSnackbar, options = {}) => {
  const token = await getToken();
  return api
    .delete(`${url}`, {
      headers: { Authorization: token },
      ...options,
    })
    .catch((err) => checkNotAuthored(err, showSnackbar));  
};

export const deleteWithBody = async (url, data, showSnackbar, options = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  return api
  .delete(`${url}`, {
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...options, 
  })
  .then(async (response) => {
    const result = await getResponseData(response, key);
    return { ...response, data: result };
  })
  .catch((err) => checkNotAuthored(err, showSnackbar));  

};

export const securePost = async (url, data, showSnackbar, options = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const isJweEnabled = process.env.REACT_APP_JWE !== 'false';
  const dataJWE = data && isJweEnabled ? await jweEncryption(data, key) : data;

  return api
    .post(`${url}`, dataJWE, {
      headers: {
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json',
      },
      ...options, 
    })
    .then(async (response) => {
      if (response.headers['content-type']?.search('application/pdf') !== -1) {
        return response;
      }
      const result = await getResponseData(response, key);
      return { ...response, data: result };
    })
    .catch((err) => checkNotAuthored(err, showSnackbar));  
};

export const secureGetPdf = async (url, showSnackbar, options = {}) => {
  const token = await getToken(); 
  const conf = {
    headers: {
      Authorization: `Bearer ${token}`, 
      Accept: 'application/pdf',      
    },
    responseType: 'blob',
    ...options,            
  };
  return apiBlob.get(url, conf).then(response => response)
  .catch((err) => checkNotAuthored(err, showSnackbar));  
};

export const PostExcel = async (url, body, showSnackbar, options = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const dataJWE = await jweEncryption(body, key); 

  return apiBlob
    .post(url, dataJWE, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.ms-excel', 
      },
      ...options,
    })
    .then((response) => response)
    .catch((err) => checkNotAuthored(err, showSnackbar));
};

export const GetExcel = async (url, showSnackbar, options = {}) => {
  const token = await getToken();

  return apiBlob
    .get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      responseType: 'blob',
      ...options, 
    })
    .then((response) => response)
    .catch((err) => checkNotAuthored(err, showSnackbar));
};

export const securePut = async (url, data, showSnackbar, options = {}) => {
  const token = await getToken();
  const key = await getJwkKeystore();
  const isJweEnabled = process.env.REACT_APP_JWE !== 'false';
  const dataJWE = data && isJweEnabled ? await jweEncryption(data, key) : data;
  return api
    .put(`${url}`, dataJWE, {
      headers: {
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json',
      },
      ...options,
    })
    .then(async (response) => {
      const result = await getResponseData(response, key);      
      return { ...response, data: result };
    })
    .catch((err) => checkNotAuthored(err, showSnackbar));  
};

export default api;
